import Day from '@awesomecomponents/day-js';
import { ActionButton, Button, Card, Tooltip, colors } from '@manulife/mux';
import { Edit as EditIcon } from '@manulife/mux-cds-icons';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import useAEMContent, { FRAGMENT_MP } from '../../CustomHooks/useAEMContent';
import useShallowEqualSelector from '../../CustomHooks/useShallowEqualSelector';
import { BoldLabel, PaddedCard, Tag } from '../Shared/SharedStyles';
import { UpdateCurrentFormDependent } from './ReducerActions/UpdateCurrentFormDependent';
import {
    calculateAge,
    canChangeDependentCob,
    canExtendDependentCoverage,
    familyMemberCobBenefits,
    isStudentStatusExtendable,
} from './utils';
import { ADOBE } from '../../utils/AppConstants';

const CardAction = styled.div`
    float: right;
`;

const CardContainer = styled.div`
    width: 100%;
`;

const CardHeaderFlex = styled.div`
    float: left;
`;

const FlexWrapper = styled.div`
    display: flex;
`;

const FamilyCard = ({ familyMember }) => {
    const history = useHistory();
    const dispatch = useDispatch();

    const { language, isLoadingMemberBenefitData, features, member, memberBenefits, allowedDependentActions } =
        useShallowEqualSelector((state) => ({
            language: state.Authentication.language,
            isLoadingMemberBenefitData: state.MemberData.isLoadingMemberBenefitData,
            features: state.Features.data,
            member: state.MemberData.member,
            memberBenefits: state.MemberData.memberBenefits,
            allowedDependentActions: state.MemberData.allowedDependentActions,
        }));

    const spouseTag = useAEMContent(FRAGMENT_MP.common, 'spouse');
    const childTag = useAEMContent(FRAGMENT_MP.common, 'child');
    const dateOfBirthLabel = useAEMContent(FRAGMENT_MP.common, 'dateOfBirthLabel');
    const otherBenefitCoverage = useAEMContent(FRAGMENT_MP.common, 'otherBenefitCoverage');
    const editButton = useAEMContent(FRAGMENT_MP.common, 'editButton');
    const toolTipButton = useAEMContent(FRAGMENT_MP.common, 'ariaTooltip');

    const extendStudentButton = useAEMContent(FRAGMENT_MP.familyMember, 'extendStudentButton');
    const studentMessage = useAEMContent(FRAGMENT_MP.familyMember, 'studentMessage');
    const coveredStudentMessage = useAEMContent(FRAGMENT_MP.familyMember, 'coveredStudentMessage');
    const childStatusTextEdited = useAEMContent(FRAGMENT_MP.familyMember, 'childStatusText', {
        replaceText: [
            {
                key: '{age}',
                value: calculateAge(familyMember.birthDate),
            },
        ],
    });
    const childAgeTextEdited = useAEMContent(FRAGMENT_MP.familyMember, 'childAgeText', {
        replaceText: [
            {
                key: '{age}',
                value: calculateAge(familyMember.birthDate),
            },
        ],
    });
    const noOtherPlanCoverage = useAEMContent(FRAGMENT_MP.familyMember, 'noOtherPlanCoverage');
    const extendCoverageTooltip = useAEMContent(FRAGMENT_MP.familyMember, 'extendCoverageTooltip', {
        replaceText: [
            {
                key: '{maxAge}',
                value: member.plan?.studentMaxAge,
            },
        ],
        parseHTML: true,
    });
    const sendAdobeAnalyticsTransaction = (transactionName) => {
            window.adobeDataLayer?.push({
                event: ADOBE.TRANSACTION_START,
                transaction: {
                    transactionName,
                    transactionCategory: 'Member Profile',
                }
            });
        };

    return (
        <PaddedCard id={`memberFamilyInformationCard-${familyMember.personId}`}>
            <Card.Header>
                <CardContainer>
                    <CardHeaderFlex>
                        <div role="heading" aria-level="2">
                            {`${familyMember.firstName} ${familyMember.lastName} `}
                            <Tag>{familyMember.familyMemberType === 'SPOUSE' ? spouseTag : childTag}</Tag>
                        </div>
                    </CardHeaderFlex>
                    {canChangeDependentCob(allowedDependentActions, features, familyMember) && (
                        <CardAction>
                            <ActionButton
                                id={`editFamilyButton-${familyMember.personId}`}
                                data-testid={`editFamilyButton-${familyMember.personId}`}
                                ariaLabel={`${editButton}${familyMember.firstName} ${familyMember.lastName}`}
                                icon={<EditIcon style={{ color: colors.m_green }} />}
                                hoverIcon={<EditIcon style={{ color: colors.dark_2_green }} />}
                                onClick={() => {
                                    sendAdobeAnalyticsTransaction('Edit My Family')
                                    history.push('/gb/member-profile-2/family/editfamily');
                                    dispatch(UpdateCurrentFormDependent(familyMember));
                                }}
                                data-analytics-click="edit-contact-information"
                            >
                                {editButton}
                            </ActionButton>
                        </CardAction>
                    )}
                </CardContainer>
            </Card.Header>
            <Card.Separator />
            <Card.Content>
                <BoldLabel noMarginTop>{dateOfBirthLabel}</BoldLabel>
                {new Day(familyMember.birthDate).format(language, 'D MMM YYYY')}
                <BoldLabel>{otherBenefitCoverage}</BoldLabel>
                {familyMemberCobBenefits(memberBenefits, familyMember.benefits).length > 0 ? (
                    familyMemberCobBenefits(memberBenefits, familyMember.benefits).map((benefit) => (
                        <div key={benefit.memberBenefitId}>{benefit.benefitName}</div>
                    ))
                ) : (
                    <div>{noOtherPlanCoverage}</div>
                )}

                {isStudentStatusExtendable(familyMember) && (
                    <>
                        <BoldLabel>
                            {familyMember.benefits.some((benefit) => benefit.covered)
                                ? coveredStudentMessage
                                : studentMessage}
                        </BoldLabel>
                    </>
                )}
                {canExtendDependentCoverage(allowedDependentActions, features, familyMember) && (
                    <>
                        <FlexWrapper>
                            <Button
                                id={`extendStudentStatus-${familyMember.personId}`}
                                data-testid={`extendStudentStatus-${familyMember.personId}`}
                                ariaLabel={`${extendStudentButton}${familyMember.firstName} ${familyMember.lastName}`}
                                variant={Button.VARIANT.SECONDARY}
                                onClick={() => {
                                    history.push('/gb/member-profile-2/family/extendcoverage');
                                    dispatch(UpdateCurrentFormDependent(familyMember));
                                }}
                                data-analytics-click="extendStudentStatus"
                                disabled={isLoadingMemberBenefitData}
                            >
                                {extendStudentButton}
                            </Button>
                            <Tooltip
                                id={`toolTip-${familyMember.personId}`}
                                variant={Tooltip.VARIANT.SECONDARY}
                                ariaLabel={`${extendStudentButton} ${toolTipButton}`}
                                content={
                                    <>
                                        {familyMember.benefits.some((benefit) => benefit.covered)
                                            ? childAgeTextEdited
                                            : childStatusTextEdited}{' '}
                                        {extendCoverageTooltip}
                                    </>
                                }
                                bubbleWidth={300}
                                customStyle={{
                                    rootStyle: {
                                        paddingLeft: '5px',
                                    },
                                }}
                            />
                        </FlexWrapper>
                    </>
                )}
            </Card.Content>
        </PaddedCard>
    );
};

export default FamilyCard;
